import { useEffect, useState } from "react";
import { Star, Pin } from "lucide-react";
import { http } from "@/lib/api";
import { toast } from "sonner";

/**
 * Inline avg + count badge that lives next to the price.
 * Pass `onCountClick` to scroll to the reviews section.
 */
export function RatingPill({ summary, onCountClick }) {
  const { avg = 0, count = 0 } = summary || {};
  const fullStars = Math.round(avg);
  return (
    <button
      type="button"
      onClick={onCountClick}
      data-testid="rating-pill"
      className="flex items-center gap-1.5 hover:opacity-80 transition-opacity cursor-pointer"
      title={count > 0 ? `Average ${avg.toFixed(1)} from ${count} review${count === 1 ? "" : "s"}` : "Be the first to rate"}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          className={i <= fullStars ? "fill-[#D4A547] text-[#D4A547]" : "text-[#9a9690]"}
        />
      ))}
      <span className="font-ui-tight text-[12px] text-[#9a9690] ml-1">
        {count > 0 ? `(${count})` : <span className="italic text-[#C9A055]/70">Be the first</span>}
      </span>
    </button>
  );
}

function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((i) => {
        const active = (hover || value) >= i;
        return (
          <button
            key={i}
            type="button"
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            onClick={() => onChange(i)}
            data-testid={`star-${i}`}
            aria-label={`${i} star${i === 1 ? "" : "s"}`}
            className="p-1 transition-transform hover:scale-110"
          >
            <Star size={26} className={active ? "fill-[#D4A547] text-[#D4A547]" : "text-[#5a5650]"} />
          </button>
        );
      })}
    </div>
  );
}

const COMMENT_MAX = 280;

/**
 * Full reviews section. Includes a "Write a review" composer + the latest reviews list.
 */
export default function ReviewsSection({ artworkId, onSummaryChange }) {
  const [summary, setSummary] = useState({ avg: 0, count: 0 });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        http.get(`/artworks/${artworkId}/reviews/summary`),
        http.get(`/artworks/${artworkId}/reviews`),
      ]);
      setSummary(s.data);
      setReviews(r.data);
      if (onSummaryChange) onSummaryChange(s.data);
    } catch (e) {
      // not fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (artworkId) load();
  }, [artworkId]);

  const submit = async () => {
    if (stars < 1 || stars > 5) {
      toast.error("Pick a star rating from 1 to 5");
      return;
    }
    if (comment.length > COMMENT_MAX) {
      toast.error(`Comment too long (max ${COMMENT_MAX} chars)`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await http.post(`/artworks/${artworkId}/reviews`, {
        stars,
        comment: comment.trim() || null,
        name: name.trim() || null,
      });
      setSummary(data.summary);
      if (onSummaryChange) onSummaryChange(data.summary);
      setReviews((prev) => [data.review, ...prev]);
      setJustSubmitted(true);
      setStars(0); setComment(""); setName("");
      toast.success("Thanks for your review!");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not submit review";
      toast.error(typeof msg === "string" ? msg : "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="mt-20 border-t border-white/10 pt-12"
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10">
          <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055]">REVIEWS</div>
          <h2 className="font-display text-3xl md:text-4xl text-[#F2EFE8] mt-2">
            What people are saying
          </h2>
          {summary.count > 0 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    size={18}
                    className={i <= Math.round(summary.avg) ? "fill-[#D4A547] text-[#D4A547]" : "text-[#5a5650]"}
                  />
                ))}
              </div>
              <span className="font-ui text-[#F2EFE8] text-[14px]">
                {summary.avg.toFixed(1)}
              </span>
              <span className="font-ui-tight text-[12px] text-[#9a9690]">
                ({summary.count} review{summary.count === 1 ? "" : "s"})
              </span>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8" data-testid="review-composer">
          {justSubmitted ? (
            <div className="text-center py-6">
              <div className="font-display text-2xl text-[#C9A055] mb-2">Thank you.</div>
              <p className="font-body text-[#B5B0A7]">Your voice matters. Mickey reads every review.</p>
              <button
                onClick={() => setJustSubmitted(false)}
                className="mt-4 font-ui text-[10px] tracking-[0.32em] text-[#8a857d] hover:text-[#C9A055] uppercase"
              >
                Submit another →
              </button>
            </div>
          ) : (
            <>
              <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] mb-4">
                RATE THIS TRUTH
              </div>
              <div className="space-y-4">
                <div>
                  <label className="font-body text-[12px] text-[#8a857d] mb-2 block">Your rating</label>
                  <StarPicker value={stars} onChange={setStars} />
                </div>
                <div>
                  <label className="font-body text-[12px] text-[#8a857d] mb-2 block">Your name (optional)</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={60}
                    placeholder="Anonymous"
                    className="w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <label className="font-body text-[12px] text-[#8a857d] mb-2 flex justify-between">
                    <span>What did this print or its story make you feel? (optional)</span>
                    <span className="text-[10px]">{comment.length}/{COMMENT_MAX}</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                    rows={4}
                    placeholder="Tell us your truth…"
                    className="w-full bg-black border border-white/15 px-3 py-2 text-[#F4F1EA] font-body text-[14px] focus:border-[#C9A055] focus:outline-none resize-none"
                    data-testid="review-comment-input"
                  />
                </div>
                <button
                  onClick={submit}
                  disabled={submitting || stars < 1}
                  data-testid="review-submit-btn"
                  className="w-full sm:w-auto px-6 py-3 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? "Submitting…" : "Submit Review"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Reviews list */}
        <div className="mt-10 space-y-4" data-testid="reviews-list">
          {loading ? (
            <div className="text-center font-body text-[#8a857d] py-6">Loading…</div>
          ) : reviews.length === 0 ? (
            <div className="text-center font-body text-[#8a857d] py-6 italic">
              No reviews yet — be the first to share your truth.
            </div>
          ) : (
            reviews.map((rv) => (
              <div
                key={rv.id}
                data-testid={`review-${rv.id}`}
                className={`border bg-[#0a0a0a] p-5 ${rv.status === "pinned" ? "border-[#C9A055]/40" : "border-white/10"}`}
              >
                <div className="flex justify-between items-start gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      {rv.status === "pinned" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#C9A055]/40 text-[#C9A055] font-ui text-[9px] tracking-[0.22em]">
                          <Pin size={9}/> FEATURED
                        </span>
                      )}
                      <div className="font-ui text-[12px] tracking-[0.18em] text-[#F4F1EA] uppercase">
                        {rv.name || "Anonymous"}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 mt-1.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={12}
                          className={i <= rv.stars ? "fill-[#D4A547] text-[#D4A547]" : "text-[#5a5650]"}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="font-ui-tight text-[10px] text-[#8a857d]">
                    {new Date(rv.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
                {rv.comment && (
                  <p className="font-body text-[14px] text-[#B5B0A7] mt-3 leading-relaxed">
                    "{rv.comment}"
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
